/*
 * The full diagnostics, which of them a student can see, by school (Sandy
 * 2026-09-16), and where each one's score lives on the UserDatabase row.
 *
 * Backend ids vs the labels people see:
 *   diagnostic-2 = "Diagnostic 1"  (readingDiagnosticScore / mathDiagnosticScore)
 *   diagnostic-3 = "Diagnostic 2"  (the *2 score fields, its own scoring table)
 *   diagnostic-4 = "Diagnostic 3"  (the *3 score fields, BBPT11 clones, IRT
 *                                   scoring, 2026-10-03)
 * scoreKey is the key the teacher and admin score reads use for that form;
 * scoreSuffix is the ending of its UserDatabase score fields (gradeDiagnostic
 * DIAGNOSTIC_FORMS). Students take the ones they see in any order (Sandy
 * 2026-10-02).
 *
 * Every school sees Diagnostic 1 and 2 (Sandy 2026-10-05: Jefferson2025 too;
 * it saw only diagnostic-3 from 2026-09-16, to keep returning students away
 * from last year's diagnostic-2). diagnostic-4 is opt-in per school: a school
 * sees it only when its list in SCHOOL_DIAGNOSTICS names it or it is in
 * D3_FIRST (below). 'No School'
 * students (signed up without a school, e.g. /start) see all three (Sandy
 * 2026-10-05). Signed out ('') is the default list. Teacher and admin pages
 * always list all three.
 *
 * Diagnostic 3 first (Sandy 2026-10-14; Erie2025 since 2026-10-04): the
 * D3_FIRST schools see only diagnostic-4 until the student finishes it or a
 * day has passed since it opened (Sandy 2026-10-16), then all three. Until
 * 1:00 AM Mountain on the school's day its card is grayed out and not
 * clickable. A page lock only: getDiagnostic does not check the date.
 *
 * Erie2025 calls its diagnostic-4 "Oct. 7 Practice PSAT" in every grade
 * (Sandy 2026-10-06; it was 10th grade only), next to Diagnostic 1 and 2 too,
 * so students can tell which one they took. A student-side name only:
 * teacher and admin pages, scores and the lambdas still call it
 * Diagnostic 3 / diagnostic-4.
 */
export const ALL_DIAGNOSTICS = [
  { id: 'diagnostic-2', label: 'Diagnostic 1', scoreKey: 'd2', scoreSuffix: '' },
  { id: 'diagnostic-3', label: 'Diagnostic 2', scoreKey: 'd3', scoreSuffix: '2' },
  { id: 'diagnostic-4', label: 'Diagnostic 3', scoreKey: 'd4', scoreSuffix: '3' },
];

export const DIAGNOSTIC_IDS = ALL_DIAGNOSTICS.map((d) => d.id);

const DEFAULT_DIAGNOSTICS = ['diagnostic-2', 'diagnostic-3'];

const SCHOOL_DIAGNOSTICS = {
  // Test schools (Sandy 2026-10-03): the first to see Diagnostic 3.
  'Mock School': DIAGNOSTIC_IDS,
  'Test School': DIAGNOSTIC_IDS,
  // students with no school (Sandy 2026-10-05): all three, any order
  'No School': DIAGNOSTIC_IDS,
};

// Diagnostic 3 first: school -> the day its diagnostic-4 opens, at 1:00 AM
// Mountain Daylight Time (UTC-6). Only diagnostic-4 until it is done or until
// 1:00 AM the next day, then all three.
const D3_FIRST = {
  Erie2025: '2026-10-07',
  Chatfield2025: '2026-10-15',
  Columbine2026: '2026-10-15',
  BearCreek2025: '2026-10-15',
  Alamosa2025: '2026-10-20',
  Thorton2025: '2026-10-21', // sic: the live school string has one n
};

// What a student sees a diagnostic called, when it differs from the usual
// label: school -> diagnostic id -> name (every grade), or -> grade -> name.
const STUDENT_LABELS = {
  Erie2025: { 'diagnostic-4': 'Oct. 7 Practice PSAT' },
};

// How long a D3_FIRST school sees only diagnostic-4 after it opens.
const D3_ONLY_MS = 24 * 60 * 60 * 1000;

const d3OpensAt = (school) => (D3_FIRST[school] ? Date.parse(`${D3_FIRST[school]}T07:00:00Z`) : undefined);

// When a diagnostic opens for a school's students (ms), or undefined. Before
// it, the intro page shows its card grayed out.
const opensAtFor = (school, id) => (id === 'diagnostic-4' ? d3OpensAt(school) : undefined);

const byId = (id) => ALL_DIAGNOSTICS.find((d) => d.id === id) || ALL_DIAGNOSTICS[0];

// Unknown ids fall back to Diagnostic 1, the same default the lambdas use.
export const diagnosticLabel = (id) => byId(id).label;
export const diagnosticScoreKey = (id) => byId(id).scoreKey;

// The name on the student's own pages. Grade can be a number or a string
// ("10", 10); anything else just gets the usual label.
export function studentDiagnosticLabel(id, school, grade) {
  const named = STUDENT_LABELS[school]?.[byId(id).id];
  const custom = typeof named === 'string' ? named : named?.[String(grade ?? '').trim()];
  return custom || byId(id).label;
}

const shownIds = (school, done, now) => {
  const opens = d3OpensAt(school);
  if (opens) return done.includes('diagnostic-4') || now >= opens + D3_ONLY_MS ? DIAGNOSTIC_IDS : ['diagnostic-4'];
  return SCHOOL_DIAGNOSTICS[school] || DEFAULT_DIAGNOSTICS;
};

// The school's list, in number order: { id, label, scoreSuffix, opensAt? }.
// done: the ids this student has finished (a D3_FIRST school shows all three
// once diagnostic-4 is among them, or a day after it opened).
export function diagnosticsForSchool(school, grade, done = [], now = Date.now()) {
  const only = shownIds(school, done, now);
  return ALL_DIAGNOSTICS.filter((d) => only.includes(d.id)).map(({ id, scoreSuffix }) => {
    const opensAt = opensAtFor(school, id);
    return { id, label: studentDiagnosticLabel(id, school, grade), scoreSuffix, ...(opensAt ? { opensAt } : {}) };
  });
}

const scaled = (attr) => {
  const n = Number(attr?.N ?? attr);
  return Number.isFinite(n) && n >= 200 && n <= 800 ? n : null;
};

// A finished diagnostic's score from the UserDatabase row (DynamoDB typed
// attributes), or null when it is not there.
export function scoreFromUser(row, diagnostic) {
  if (!row || !diagnostic) return null;
  const suffix = diagnostic.scoreSuffix ?? byId(diagnostic.id).scoreSuffix;
  const reading = scaled(row[`readingDiagnosticScore${suffix}`]);
  const math = scaled(row[`mathDiagnosticScore${suffix}`]);
  if (reading == null && math == null) return null;
  return { reading, math, total: reading != null && math != null ? reading + math : null };
}
